import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
  Icon,
} from "@chakra-ui/react";
import { FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { BaseBlockProps } from "./types";

interface TableContent {
  headers?: string[];
  rows?: string[][];
}

// Helper to detect and render URLs in cell values
const renderCellValue = (
  value: unknown,
  navigate: ReturnType<typeof useNavigate>,
) => {
  const text = String(value);

  // Check if it's a URL
  const urlRegex = /^(https?:\/\/[^\s]+)$/;
  const internalPathRegex = /^\/[^\s]*$/;

  if (urlRegex.test(text)) {
    const displayText = text.length > 40 ? text.substring(0, 37) + "..." : text;
    return (
      <Link
        href={text}
        isExternal
        color="p.600"
        fontSize="xs"
        display="inline-flex"
        alignItems="center"
        gap={1}
        _hover={{ color: "p.700", textDecoration: "underline" }}
      >
        {displayText}
        <Icon as={FiExternalLink} boxSize={2.5} />
      </Link>
    );
  }

  if (internalPathRegex.test(text)) {
    return (
      <Link
        href={text}
        onClick={(e) => {
          e.preventDefault();
          navigate(text);
        }}
        color="p.600"
        fontSize="xs"
        _hover={{ color: "p.700", textDecoration: "underline" }}
      >
        {text}
      </Link>
    );
  }

  return text;
};

export const TableBlock = ({ block }: BaseBlockProps) => {
  const navigate = useNavigate();

  // Handle both flat and nested content structure
  const content = block.content as TableContent | undefined;
  const headers = content?.headers || (block.headers as string[]) || [];
  const rows = content?.rows || (block.rows as string[][]) || [];

  if (headers.length === 0 && rows.length === 0) {
    return null;
  }

  return (
    <Box
      borderRadius="component.md"
      overflow="hidden"
      border="1px solid"
      borderColor="border.default"
      maxW="100%"
      overflowX="auto"
    >
      <TableContainer>
        <Table size="sm" variant="simple">
          {headers.length > 0 && (
            <Thead bg="neutral.50">
              <Tr>
                {headers.map((header, i) => (
                  <Th
                    key={i}
                    fontSize="xs"
                    textTransform="none"
                    letterSpacing="normal"
                    fontWeight="600"
                    color="text.body.primary"
                    py={2}
                  >
                    {header}
                  </Th>
                ))}
              </Tr>
            </Thead>
          )}
          <Tbody>
            {rows.map((row, rowIndex) => (
              <Tr key={rowIndex} _hover={{ bg: "neutral.50" }}>
                {(row as unknown[]).map((cell, cellIndex) => (
                  <Td
                    key={cellIndex}
                    fontSize="xs"
                    color="text.body.primary"
                    py={2}
                  >
                    {renderCellValue(cell, navigate)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
